/**
 * German copy for every refusal the **event creation gate** (#664) can answer with.
 *
 * Two entry points feed it, and that is the reason it is a module rather than a `switch` inside the component:
 *
 *  * `GET Inventory/Event/GetCreationAllowance` answers a `refusalCode` **as data** — a 200 whose
 *    `allowedKinds` is empty. The form renders the sentence under the date fields *before* anything is
 *    submitted;
 *  * `Add` / `AddMany` (and the `dryRun` variant) answer the *same* codes as a structured `ApiError`.
 *
 * The same rule must read identically in both places, or the form explains one thing and the submit refusal
 * another. Per the style guide (*"Surfacing an API error — branch on `code`, never render `description`"*) the
 * server's English `description` is never rendered: it is developer-facing, and these refusals are exactly the
 * sentences an administrator has to act on.
 *
 * Addressing is the formal `Sie`.
 *
 * **When a new `E_EVENT_*` code appears**, add it here with a sentence taken from that code's XML doc in
 * `API.Core/Models/Event/EventErrorCodes.cs` — not a transliteration of the catalog's one-line English
 * summary — and never widen {@link describeEventCreationError}'s fallback to `description`.
 */

import {readErrorCode} from '../../../_auth/two-factor/second-factor-errors';

/** The `E_EVENT_*` codes, spelled out exactly as the server's `EventErrorCodes` registers them. */
export const EventCreationErrorCodes = {
  /** Some day between start and end is covered by no contract at all. `details.firstUncoveredDate` names it. */
  ContractGap: 'E_EVENT_CONTRACT_GAP',
  /** A covering contract licenses neither registers nor virtual events, so nothing may be created. */
  NotLicensed: 'E_EVENT_NOT_LICENSED',
  /** Every virtual slot of a covering contract is already taken for this window. */
  VirtualLimitReached: 'E_EVENT_VIRTUAL_LIMIT_REACHED',
  /** Another Kassen-Event already runs on this shard inside the window. */
  RealOverlap: 'E_EVENT_REAL_OVERLAP',
  /** The requested kind is not allowed at this date (e.g. a Real event moved into a 0-device period). */
  KindNotAllowedAtDate: 'E_EVENT_KIND_NOT_ALLOWED_AT_DATE',
  /** A sales-shaped operation (download, summary) was asked of a virtual event, which never has sales. */
  VirtualHasNoSales: 'E_EVENT_VIRTUAL_HAS_NO_SALES'
} as const;

/**
 * `ApiError.code` → German sentence.
 *
 * Each one names the rule **and** what to do about it, because none of them is discoverable from the form that
 * was just refused: "kein Vertrag" and "Vertrag ohne Geräte" look identical from the outside, and both look
 * like a broken screen if the message only says that creation failed.
 */
const EVENT_CREATION_MESSAGES: Readonly<Record<string, string>> = {
  [EventCreationErrorCodes.ContractGap]:
    'Für mindestens einen Tag in diesem Zeitraum besteht kein Vertrag. Ein Event darf keine vertragsfreie ' +
    'Lücke überspannen — bitte passen Sie den Zeitraum an oder hinterlegen Sie einen durchgehenden Vertrag.',
  [EventCreationErrorCodes.NotLicensed]:
    'Ihr Vertrag enthält in diesem Zeitraum weder Geräte noch virtuelle Events. Es kann daher kein Event ' +
    'angelegt werden. Bitte wenden Sie sich an Ihren Ansprechpartner.',
  [EventCreationErrorCodes.VirtualLimitReached]:
    'Die im Vertrag enthaltenen virtuellen Events sind in diesem Zeitraum bereits vollständig belegt. ' +
    'Beenden Sie ein bestehendes virtuelles Event oder wählen Sie einen anderen Zeitraum.',
  [EventCreationErrorCodes.RealOverlap]:
    'In diesem Zeitraum läuft an diesem Standort bereits ein Kassen-Event. Pro Standort kann immer nur ein ' +
    'Kassen-Event gleichzeitig laufen.',
  [EventCreationErrorCodes.KindNotAllowedAtDate]:
    'Die gewählte Art des Events ist in diesem Zeitraum vertraglich nicht möglich. Bitte prüfen Sie den ' +
    'Zeitraum oder die Art des Events.',
  [EventCreationErrorCodes.VirtualHasNoSales]:
    'Virtuelle Events haben keine Umsätze — für sie stehen weder Auswertungen noch Downloads zur Verfügung.'
};

/**
 * The gap refusal is the one whose usefulness depends on a value in `details`: "irgendwo in diesem Zeitraum"
 * is not something anybody can act on, "ab dem 01.08.2026" is. Rendered `dd.MM.yyyy`, matching every other
 * date on the events screen.
 */
function gapSuffix(gapDate: string | null | undefined): string {
  if (!gapDate) return '';
  const day = new Date(gapDate);
  if (isNaN(day.getTime())) return '';
  const pad = (value: number): string => (value < 10 ? '0' : '') + value;
  return ` Erster nicht abgedeckter Tag: ${pad(day.getDate())}.${pad(day.getMonth() + 1)}.${day.getFullYear()}.`;
}

/** The German fallback for a refusal this table does not recognise. Never the server's `description`. */
export const EVENT_CREATION_FALLBACK_MESSAGE =
  'Das Event konnte nicht angelegt werden. Bitte prüfen Sie Zeitraum und Art des Events.';

/**
 * The sentence for a refusal code the **allowance** reported as data (a 200 with an empty `allowedKinds`).
 *
 * @param code    the `refusalCode` the allowance carries, or `null`/`undefined` when nothing was refused.
 * @param gapDate the allowance's `gapDate`, appended to the gap refusal so it names the day.
 * @returns the German sentence, or `undefined` when there is nothing to say.
 */
export function describeEventCreationRefusal(code: string | null | undefined,
                                             gapDate: string | null | undefined = null): string | undefined {
  if (!code) return undefined;
  const message = EVENT_CREATION_MESSAGES[code] ?? EVENT_CREATION_FALLBACK_MESSAGE;
  return code === EventCreationErrorCodes.ContractGap ? message + gapSuffix(gapDate) : message;
}

/**
 * The sentence for a failed create/dry-run call.
 *
 * @param err      whatever rxjs handed the `error` callback.
 * @param fallback the sentence for a failure this table does not recognise. Per call site, because most of
 *                 this controller's other refusals (an empty name, a missing permission) come back as bare
 *                 statuses with no `ApiError` body at all.
 */
export function describeEventCreationError(err: unknown,
                                           fallback: string = EVENT_CREATION_FALLBACK_MESSAGE): string {
  const code = readErrorCode(err);
  if (!code) return fallback;

  const message = EVENT_CREATION_MESSAGES[code];
  if (message === undefined) return fallback;

  // `details.firstUncoveredDate` travels with the gap refusal; same sentence as the allowance path produces.
  const details = (err as {error?: {details?: {firstUncoveredDate?: string}}} | null)?.error?.details;
  return code === EventCreationErrorCodes.ContractGap ? message + gapSuffix(details?.firstUncoveredDate) : message;
}
